/**
 * Integration Entity - RTK Query API
 * API endpoints for Integration Connectors domain using RTK Query with Django REST Framework
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQueryWithReauth } from '../../../shared/api/baseQuery';
import { fetchAllPages } from '../../../shared/api/paginate';
import { archiveArgs } from '../../../shared/api/archiveArgs';

const normalizeListResponse = (response) => ({
  data: response?.results || response || [],
  total: response?.count || response?.length || 0,
});

const extractResults = (response) => response?.results || response || [];

const fetchProviders = ({ type = '', search = '' } = {}, baseQuery) =>
  fetchAllPages(
    baseQuery,
    '/api/v1/integrations/providers/',
    {
      provider_type: type || undefined,
      search: search || undefined,
    },
    { pageSize: 500, maxPages: 20 }
  );

export const integrationApiTestUtils = {
  normalizeListResponse,
  extractResults,
  fetchProviders,
};

export const integrationApi = createApi({
  reducerPath: 'integrationApi',
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    'IntegrationProvider',
    'TenantIntegration',
    'IntegrationCredential',
    'IntegrationJob',
    'WebhookEndpoint',
    'WebhookEvent',
    'DataMapping',
    // INT-05
    'AutomationRule',
    'AutomationExecution',
    'WebhookDelivery',
  ],
  endpoints: (builder) => ({
    // =========================================================================
    // PROVIDERS
    // =========================================================================

    /**
     * Fetch up to 10,000 available integration providers across pages.
     */
    getProviders: builder.query({
      queryFn: (args, _api, _extraOptions, baseQuery) => fetchProviders(args, baseQuery),
      providesTags: [{ type: 'IntegrationProvider', id: 'LIST' }],
    }),

    /**
     * Get provider by ID
     */
    getProviderById: builder.query({
      query: (id) => `/api/v1/integrations/providers/${id}/`,
      providesTags: (result, error, id) => [{ type: 'IntegrationProvider', id }],
    }),

    /**
     * Get providers grouped by type
     */
    getProvidersByType: builder.query({
      query: () => '/api/v1/integrations/providers/by_type/',
      providesTags: [{ type: 'IntegrationProvider', id: 'BY_TYPE' }],
    }),

    // =========================================================================
    // TENANT INTEGRATIONS
    // =========================================================================

    /**
     * Get tenant's integrations
     */
    /**
     * GAP-INT-PAGING: every tenant integration across all pages (the paged
     * getIntegrations below shows only one page, so a tenant with more than a
     * page of integrations used to see a silently truncated list).
     * Same `{ data, total }` shape as getIntegrations.
     */
    getAllIntegrations: builder.query({
      queryFn: async ({ status = '', provider = '' } = {}, _api, _extra, baseQuery) => {
        const result = await fetchAllPages(baseQuery, '/api/v1/integrations/integrations/', {
          status: status || undefined,
          provider: provider || undefined,
        });
        if (result.error) return { error: result.error };
        return {
          data: {
            data: result.data,
            total: result.data.length,
            truncated: Boolean(result.meta?.truncated),
          },
        };
      },
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'TenantIntegration', id })),
              { type: 'TenantIntegration', id: 'LIST' },
            ]
          : [{ type: 'TenantIntegration', id: 'LIST' }],
    }),

    getIntegrations: builder.query({
      query: ({ page = 1, limit = 20, status = '', provider = '' } = {}) => ({
        url: '/api/v1/integrations/integrations/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          provider: provider || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'TenantIntegration', id })),
              { type: 'TenantIntegration', id: 'LIST' },
            ]
          : [{ type: 'TenantIntegration', id: 'LIST' }],
    }),

    /**
     * Get integration by ID
     */
    getIntegrationById: builder.query({
      query: (id) => `/api/v1/integrations/integrations/${id}/`,
      providesTags: (result, error, id) => [{ type: 'TenantIntegration', id }],
    }),

    /**
     * Create new integration
     */
    createIntegration: builder.mutation({
      query: (data) => ({
        url: '/api/v1/integrations/integrations/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'TenantIntegration', id: 'LIST' }],
    }),

    /**
     * Update integration
     */
    updateIntegration: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/integrations/integrations/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'TenantIntegration', id },
        { type: 'TenantIntegration', id: 'LIST' },
      ],
    }),

    /**
     * Delete integration
     */
    deleteIntegration: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/integrations/integrations/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'TenantIntegration', id: 'LIST' }],
    }),

    /**
     * Test integration connection
     */
    testConnection: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/integrations/${id}/test_connection/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'TenantIntegration', id }],
    }),

    /**
     * Trigger immediate sync
     */
    syncNow: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/integrations/${id}/sync_now/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'TenantIntegration', id },
        { type: 'IntegrationJob', id: 'LIST' },
      ],
    }),

    /**
     * Enable integration
     */
    enableIntegration: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/integrations/${id}/enable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'TenantIntegration', id }],
    }),

    /**
     * Disable integration
     */
    disableIntegration: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/integrations/${id}/disable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'TenantIntegration', id }],
    }),

    /**
     * Get active integrations
     */
    getActiveIntegrations: builder.query({
      query: () => '/api/v1/integrations/integrations/active/',
      transformResponse: extractResults,
      providesTags: [{ type: 'TenantIntegration', id: 'ACTIVE' }],
    }),

    /**
     * INT-05: integrations due a sync (active, sync enabled, next_sync_at passed).
     * The router prefix and the viewset are both "integrations", hence the doubled path.
     */
    getPendingSyncIntegrations: builder.query({
      query: () => '/api/v1/integrations/integrations/pending_sync/',
      transformResponse: extractResults,
      providesTags: [{ type: 'TenantIntegration', id: 'PENDING_SYNC' }],
    }),

    // =========================================================================
    // CREDENTIALS
    // =========================================================================

    /**
     * Get credentials for an integration
     */
    getCredentials: builder.query({
      query: (integrationId) => ({
        url: '/api/v1/integrations/credentials/',
        params: { integration: integrationId },
      }),
      transformResponse: extractResults,
      providesTags: (result, error, integrationId) => [
        { type: 'IntegrationCredential', id: integrationId },
      ],
    }),

    /**
     * Add credential
     */
    addCredential: builder.mutation({
      query: (data) => ({
        url: '/api/v1/integrations/credentials/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: (result, error, { integration }) => [
        { type: 'IntegrationCredential', id: integration },
      ],
    }),

    /**
     * Update credential
     */
    updateCredential: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/integrations/credentials/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: [{ type: 'IntegrationCredential' }],
    }),

    /**
     * Rotate credential
     */
    rotateCredential: builder.mutation({
      query: ({ id, value, expiresAt }) => ({
        url: `/api/v1/integrations/credentials/${id}/rotate/`,
        method: 'POST',
        body: { value, expires_at: expiresAt },
      }),
      invalidatesTags: [{ type: 'IntegrationCredential' }],
    }),

    /**
     * Delete credential
     */
    deleteCredential: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/credentials/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'IntegrationCredential' }],
    }),

    // =========================================================================
    // JOBS
    // =========================================================================

    /**
     * Get integration jobs
     */
    getJobs: builder.query({
      query: ({ page = 1, limit = 20, integration = '', status = '', operation = '' } = {}) => ({
        url: '/api/v1/integrations/jobs/',
        params: {
          page,
          page_size: limit,
          integration: integration || undefined,
          status: status || undefined,
          operation: operation || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'IntegrationJob', id })),
              { type: 'IntegrationJob', id: 'LIST' },
            ]
          : [{ type: 'IntegrationJob', id: 'LIST' }],
    }),

    /**
     * Get job by ID
     */
    getJobById: builder.query({
      query: (id) => `/api/v1/integrations/jobs/${id}/`,
      providesTags: (result, error, id) => [{ type: 'IntegrationJob', id }],
    }),

    /**
     * Cancel job
     */
    cancelJob: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/jobs/${id}/cancel/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'IntegrationJob', id },
        { type: 'IntegrationJob', id: 'LIST' },
      ],
    }),

    /**
     * Retry failed job
     */
    retryJob: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/jobs/${id}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'IntegrationJob', id: 'LIST' }],
    }),

    /**
     * Get running jobs
     */
    getRunningJobs: builder.query({
      query: () => '/api/v1/integrations/jobs/running/',
      transformResponse: extractResults,
      providesTags: [{ type: 'IntegrationJob', id: 'RUNNING' }],
    }),

    /**
     * Get failed jobs
     */
    getFailedJobs: builder.query({
      query: () => '/api/v1/integrations/jobs/failed/',
      transformResponse: extractResults,
      providesTags: [{ type: 'IntegrationJob', id: 'FAILED' }],
    }),

    /**
     * INT-05: jobs in "retrying" whose next_retry_at has passed.
     */
    getPendingRetryJobs: builder.query({
      query: () => '/api/v1/integrations/jobs/pending_retry/',
      transformResponse: extractResults,
      providesTags: [{ type: 'IntegrationJob', id: 'PENDING_RETRY' }],
    }),

    /**
     * Get job logs
     */
    getJobLogs: builder.query({
      query: (jobId) => ({
        url: '/api/v1/integrations/job-logs/',
        params: { job: jobId },
      }),
      transformResponse: extractResults,
    }),

    // =========================================================================
    // WEBHOOKS
    // =========================================================================

    /**
     * Get webhook endpoints
     */
    getWebhooks: builder.query({
      query: ({ integration = '', status = '' } = {}) => ({
        url: '/api/v1/integrations/webhooks/',
        params: {
          integration: integration || undefined,
          status: status || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'WebhookEndpoint', id })),
              { type: 'WebhookEndpoint', id: 'LIST' },
            ]
          : [{ type: 'WebhookEndpoint', id: 'LIST' }],
    }),

    /**
     * Get webhook by ID
     */
    getWebhookById: builder.query({
      query: (id) => `/api/v1/integrations/webhooks/${id}/`,
      providesTags: (result, error, id) => [{ type: 'WebhookEndpoint', id }],
    }),

    /**
     * Create webhook endpoint
     */
    createWebhook: builder.mutation({
      query: (data) => ({
        url: '/api/v1/integrations/webhooks/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'WebhookEndpoint', id: 'LIST' }],
    }),

    /**
     * Update webhook
     */
    updateWebhook: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/integrations/webhooks/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'WebhookEndpoint', id },
        { type: 'WebhookEndpoint', id: 'LIST' },
      ],
    }),

    /**
     * Delete webhook
     */
    deleteWebhook: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/integrations/webhooks/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'WebhookEndpoint', id: 'LIST' }],
    }),

    /**
     * Regenerate webhook secret
     */
    regenerateWebhookSecret: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/webhooks/${id}/regenerate_secret/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'WebhookEndpoint', id }],
    }),

    /**
     * Enable webhook
     */
    enableWebhook: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/webhooks/${id}/enable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'WebhookEndpoint', id }],
    }),

    /**
     * Disable webhook
     */
    disableWebhook: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/webhooks/${id}/disable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [{ type: 'WebhookEndpoint', id }],
    }),

    /**
     * Get webhook events
     */
    getWebhookEvents: builder.query({
      query: ({ endpoint = '', status = '', page = 1, limit = 50 } = {}) => ({
        url: '/api/v1/integrations/webhook-events/',
        params: {
          endpoint: endpoint || undefined,
          status: status || undefined,
          page,
          page_size: limit,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'WebhookEvent', id: 'LIST' }],
    }),

    /**
     * Reprocess webhook event
     */
    reprocessWebhookEvent: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/webhook-events/${id}/reprocess/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'WebhookEvent', id: 'LIST' }],
    }),

    // =========================================================================
    // AUTOMATION (INT-05)
    // =========================================================================

    getAutomationRules: builder.query({
      query: ({ page = 1, limit = 50, status = '', search = '' } = {}) => ({
        url: '/api/v1/integrations/automation-rules/',
        params: {
          page,
          page_size: limit,
          status: status || undefined,
          search: search || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'AutomationRule', id })),
              { type: 'AutomationRule', id: 'LIST' },
            ]
          : [{ type: 'AutomationRule', id: 'LIST' }],
    }),

    createAutomationRule: builder.mutation({
      query: (data) => ({
        url: '/api/v1/integrations/automation-rules/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'AutomationRule', id: 'LIST' }],
    }),

    updateAutomationRule: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/integrations/automation-rules/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AutomationRule', id },
        { type: 'AutomationRule', id: 'LIST' },
      ],
    }),

    deleteAutomationRule: builder.mutation({
      // D24: archives; optional { id, reason } sends it in the request body.
      query: (arg) => {
        const { id, body } = archiveArgs(arg);
        return { url: `/api/v1/integrations/automation-rules/${id}/`, method: 'DELETE', body };
      },
      invalidatesTags: [{ type: 'AutomationRule', id: 'LIST' }],
    }),

    enableAutomationRule: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/automation-rules/${id}/enable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'AutomationRule', id },
        { type: 'AutomationRule', id: 'LIST' },
      ],
    }),

    disableAutomationRule: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/automation-rules/${id}/disable/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'AutomationRule', id },
        { type: 'AutomationRule', id: 'LIST' },
      ],
    }),

    /**
     * Queue a manual run. The backend refuses (400) unless the rule is enabled.
     */
    runAutomationRule: builder.mutation({
      query: ({ id, payload = {} }) => ({
        url: `/api/v1/integrations/automation-rules/${id}/run/`,
        method: 'POST',
        body: { payload },
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'AutomationRule', id },
        { type: 'AutomationRule', id: 'LIST' },
        { type: 'AutomationExecution', id: 'LIST' },
        { type: 'WebhookDelivery', id: 'LIST' },
      ],
    }),

    getAutomationExecutions: builder.query({
      query: ({ rule = '', status = '', page = 1, limit = 50 } = {}) => ({
        url: '/api/v1/integrations/automation-executions/',
        params: {
          rule: rule || undefined,
          status: status || undefined,
          page,
          page_size: limit,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'AutomationExecution', id: 'LIST' }],
    }),

    getWebhookDeliveries: builder.query({
      query: ({ status = '', endpoint = '', page = 1, limit = 50 } = {}) => ({
        url: '/api/v1/integrations/webhook-deliveries/',
        params: {
          status: status || undefined,
          endpoint: endpoint || undefined,
          page,
          page_size: limit,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: [{ type: 'WebhookDelivery', id: 'LIST' }],
    }),

    /**
     * Retry a failed delivery. The backend refuses (400) any other status.
     */
    retryWebhookDelivery: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/webhook-deliveries/${id}/retry/`,
        method: 'POST',
      }),
      invalidatesTags: [{ type: 'WebhookDelivery', id: 'LIST' }],
    }),

    // =========================================================================
    // DATA MAPPINGS
    // =========================================================================

    /**
     * Get data mappings
     */
    getMappings: builder.query({
      query: ({ integration = '', entityType = '', direction = '' } = {}) => ({
        url: '/api/v1/integrations/mappings/',
        params: {
          integration: integration || undefined,
          entity_type: entityType || undefined,
          direction: direction || undefined,
        },
      }),
      transformResponse: normalizeListResponse,
      providesTags: (result) =>
        result?.data
          ? [
              ...result.data.map(({ id }) => ({ type: 'DataMapping', id })),
              { type: 'DataMapping', id: 'LIST' },
            ]
          : [{ type: 'DataMapping', id: 'LIST' }],
    }),

    /**
     * Get mapping by ID
     */
    getMappingById: builder.query({
      query: (id) => `/api/v1/integrations/mappings/${id}/`,
      providesTags: (result, error, id) => [{ type: 'DataMapping', id }],
    }),

    /**
     * Create data mapping
     */
    createMapping: builder.mutation({
      query: (data) => ({
        url: '/api/v1/integrations/mappings/',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: [{ type: 'DataMapping', id: 'LIST' }],
    }),

    /**
     * Update mapping
     */
    updateMapping: builder.mutation({
      query: ({ id, ...data }) => ({
        url: `/api/v1/integrations/mappings/${id}/`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (result, error, { id }) => [
        { type: 'DataMapping', id },
        { type: 'DataMapping', id: 'LIST' },
      ],
    }),

    /**
     * Delete mapping
     */
    deleteMapping: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/mappings/${id}/`,
        method: 'DELETE',
      }),
      invalidatesTags: [{ type: 'DataMapping', id: 'LIST' }],
    }),

    /**
     * Test data mapping
     */
    testMapping: builder.mutation({
      query: ({ id, sampleData }) => ({
        url: `/api/v1/integrations/mappings/${id}/test/`,
        method: 'POST',
        body: { sample_data: sampleData },
      }),
    }),

    /**
     * Toggle mapping active status
     */
    toggleMapping: builder.mutation({
      query: (id) => ({
        url: `/api/v1/integrations/mappings/${id}/toggle/`,
        method: 'POST',
      }),
      invalidatesTags: (result, error, id) => [
        { type: 'DataMapping', id },
        { type: 'DataMapping', id: 'LIST' },
      ],
    }),
  }),
});

// Export hooks for components
export const {
  // Providers
  useGetProvidersQuery,
  useGetProviderByIdQuery,
  useGetProvidersByTypeQuery,
  // Integrations
  useGetIntegrationsQuery,
  useGetAllIntegrationsQuery,
  useGetIntegrationByIdQuery,
  useCreateIntegrationMutation,
  useUpdateIntegrationMutation,
  useDeleteIntegrationMutation,
  useTestConnectionMutation,
  useSyncNowMutation,
  useEnableIntegrationMutation,
  useDisableIntegrationMutation,
  useGetActiveIntegrationsQuery,
  useGetPendingSyncIntegrationsQuery,
  // Credentials
  useGetCredentialsQuery,
  useAddCredentialMutation,
  useUpdateCredentialMutation,
  useRotateCredentialMutation,
  useDeleteCredentialMutation,
  // Jobs
  useGetJobsQuery,
  useGetJobByIdQuery,
  useCancelJobMutation,
  useRetryJobMutation,
  useGetRunningJobsQuery,
  useGetFailedJobsQuery,
  useGetJobLogsQuery,
  useGetPendingRetryJobsQuery,
  // Webhooks
  useGetWebhooksQuery,
  useGetWebhookByIdQuery,
  useCreateWebhookMutation,
  useUpdateWebhookMutation,
  useDeleteWebhookMutation,
  useRegenerateWebhookSecretMutation,
  useEnableWebhookMutation,
  useDisableWebhookMutation,
  useGetWebhookEventsQuery,
  useReprocessWebhookEventMutation,
  // Automation (INT-05)
  useGetAutomationRulesQuery,
  useCreateAutomationRuleMutation,
  useUpdateAutomationRuleMutation,
  useDeleteAutomationRuleMutation,
  useEnableAutomationRuleMutation,
  useDisableAutomationRuleMutation,
  useRunAutomationRuleMutation,
  useGetAutomationExecutionsQuery,
  useGetWebhookDeliveriesQuery,
  useRetryWebhookDeliveryMutation,
  // Mappings
  useGetMappingsQuery,
  useGetMappingByIdQuery,
  useCreateMappingMutation,
  useUpdateMappingMutation,
  useDeleteMappingMutation,
  useTestMappingMutation,
  useToggleMappingMutation,
} = integrationApi;
